import React, { useCallback, useState, memo } from 'react';
import { View, Pressable, Platform, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { useTranslation } from 'react-i18next';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { responsivePadding } from '@stylesheet';
import Pagination from '@components/Global/Pagination/Pagination';
import type { SearchBarPaginationConfig } from '../Types';
import { Text } from '@constants/Themed';

type SearchBarPaginationInlineProps = {
  config: SearchBarPaginationConfig;
  theme: boolean;
  buttonSize: number;
  iconSize: number;
  borderRadius: number;
  /** When true, shrink-wrap on the search row instead of stretching full width below */
  inline?: boolean;
  /** Controlled expanded state (full controls). Use with `onExpandedChange` when `inline` so the parent can render Pagination below the toolbar row. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

/**
 * Mini pager (← page →) with caret to expand full Pagination controls below.
 */
function SearchBarPaginationInline({
  config,
  theme,
  buttonSize,
  iconSize,
  borderRadius,
  inline = false,
  expanded: expandedControlled,
  onExpandedChange,
}: SearchBarPaginationInlineProps) {
  const { t } = useTranslation();
  const { pagination, onPageChange } = config;
  const [expandedInternal, setExpandedInternal] = useState(false);
  const isControlled = onExpandedChange != null;
  const expanded = isControlled ? Boolean(expandedControlled) : expandedInternal;
  const setExpanded = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      if (isControlled) {
        const value = typeof next === 'function' ? next(expanded) : next;
        onExpandedChange(value);
      } else {
        setExpandedInternal(next);
      }
    },
    [isControlled, onExpandedChange, expanded],
  );

  const goPrev = useCallback(() => {
    if (pagination.hasPrevPage) onPageChange(pagination.currentPage - 1);
  }, [pagination, onPageChange]);

  const goNext = useCallback(() => {
    if (pagination.hasNextPage) onPageChange(pagination.currentPage + 1);
  }, [pagination, onPageChange]);

  if (
    !pagination ||
    typeof pagination.currentPage !== 'number' ||
    typeof pagination.totalPages !== 'number'
  ) {
    return null;
  }

  const btn = (enabled: boolean, onPress: () => void, icon: 'chevron-left' | 'chevron-right') => (
    <Pressable
      onPress={onPress}
      disabled={!enabled}
      style={({ pressed }) => [
        styles.miniBtn,
        {
          width: buttonSize * 0.85,
          height: buttonSize * 0.85,
          borderRadius: borderRadius / 2,
          opacity: enabled ? (pressed ? 0.85 : 1) : 0.4,
          backgroundColor: theme ? '#f3f4f6' : '#374151',
          borderColor: theme ? '#e5e7eb' : '#4b5563',
        },
      ]}
      accessibilityRole="button"
      accessibilityLabel={icon === 'chevron-left' ? t('paginationPreviousPage') : t('paginationNextPage')}
    >
      <Feather
        name={icon}
        size={iconSize * 0.65}
        color={theme ? '#374151' : '#e5e7eb'}
      />
    </Pressable>
  );

  return (
    <View style={inline ? styles.columnInline : styles.column}>
      <View style={inline ? styles.miniRowInline : styles.miniRow}>
        {btn(pagination.hasPrevPage, goPrev, 'chevron-left')}
        <Text
          style={{
            fontSize: responsiveFontSize(12),
            fontWeight: '600',
            color: theme ? '#374151' : '#e5e7eb',
            minWidth: responsivePadding(36),
            textAlign: 'center',
          }}
        >
          {t('paginationPageShort', {
            current: pagination.currentPage,
            total: pagination.totalPages,
          })}
        </Text>
        {btn(pagination.hasNextPage, goNext, 'chevron-right')}
        <Pressable
          onPress={() => setExpanded((prev) => !prev)}
          style={({ pressed }) => [
            styles.caretBtn,
            {
              width: buttonSize * 0.85,
              height: buttonSize * 0.85,
              borderRadius: borderRadius / 2,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
              borderColor: theme ? '#e5e7eb' : '#4b5563',
              opacity: pressed ? 0.85 : 1,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel={expanded ? t('paginationCollapseControls') : t('paginationExpandControls')}
          accessibilityState={{ expanded }}
        >
          <Feather
            name="chevron-down"
            size={iconSize * 0.65}
            color={theme ? '#374151' : '#e5e7eb'}
            style={{
              transform: [{ rotate: expanded ? '180deg' : '0deg' }],
            }}
          />
        </Pressable>
      </View>
      {expanded && !inline ? (
        <Animatable.View
          animation="fadeInDown"
          duration={280}
          useNativeDriver={Platform.OS !== 'web'}
          style={styles.expandedWrap}
        >
          <Pagination
            {...config}
            style={
              Platform.OS === 'web'
                ? ({ marginTop: responsivePadding(8), width: '100%' } as any)
                : { marginTop: responsivePadding(8) }
            }
          />
        </Animatable.View>
      ) : null}
    </View>
  );
}

/** Full pagination block for hamburger modal footer */
export function SearchBarPaginationModalBlock({
  config,
}: {
  config: SearchBarPaginationConfig;
}) {
  return (
    <View
      style={{
        width: '100%',
        marginTop: responsivePadding(12),
        paddingTop: responsivePadding(12),
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: 'rgba(128,128,128,0.35)',
      }}
    >
      <Pagination
        {...config}
        style={{ width: '100%' }}
      />
    </View>
  );
}

export default memo(SearchBarPaginationInline);

const styles = StyleSheet.create({
  column: {
    width: '100%',
    flexShrink: 0,
    alignItems: 'stretch',
    minWidth: 0,
  },
  columnInline: {
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    maxWidth: '100%',
  },
  miniRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    width: '100%',
    gap: responsivePadding(4),
  },
  miniRowInline: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    gap: responsivePadding(4),
    alignSelf: 'center',
  },
  miniBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  caretBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  expandedWrap: {
    overflow: 'hidden',
    width: '100%',
  },
});
